import React, { useState, useEffect } from 'react';
import useAuthStore from '../stores/authStore';
import './Profile.css';

// Helper function to handle 401 errors
const handleUnauthorized = async () => {
  const { default: useAuthStoreModule } = await import('../stores/authStore');
  useAuthStoreModule.getState().signout();
  window.location.href = '/signin';
};

const Profile = () => {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeSection, setActiveSection] = useState('profile');

  // Profile form state
  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    full_name: '',
    location: '',
    linkedin_link: '',
    yc_link: '',
    level: ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  useEffect(() => {
    // Handle hash-based navigation on mount
    const hash = window.location.hash.slice(1); // Remove the '#' character
    if (hash === 'upgrade-level') {
      setActiveSection('upgrade');
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    if (activeSection === 'sessions') {
      fetchSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setProfileData({
          username: data.user.username || '',
          email: data.user.email || '',
          full_name: data.user.full_name || '',
          location: data.user.location || '',
          linkedin_link: data.user.linkedin_link || '',
          yc_link: data.user.yc_link || '',
          level: data.user.level || ''
        });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to load profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Profile updated successfully' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setSaving(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      setSaving(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully' });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to change password' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/sessions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setSessions(data.sessions);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to load sessions' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load sessions' });
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to revoke this session?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Session revoked successfully' });
        fetchSessions(); // Refresh the list
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to revoke session' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to revoke session' });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>Profile Settings</h1>
        <p>Manage your account information and security</p>
      </div>

      {message.text && (
        <div className={`profile-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="profile-layout">
        {/* Left Sidebar */}
        <div className="profile-sidebar">
          <button
            className={`sidebar-item ${activeSection === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveSection('profile')}
          >
            <span>Profile Information</span>
          </button>
          <button
            className={`sidebar-item ${activeSection === 'password' ? 'active' : ''}`}
            onClick={() => setActiveSection('password')}
          >
            <span>Change Password</span>
          </button>
          <button
            className={`sidebar-item ${activeSection === 'sessions' ? 'active' : ''}`}
            onClick={() => setActiveSection('sessions')}
          >
            <span>Active Sessions</span>
          </button>
          <button
            className={`sidebar-item ${activeSection === 'upgrade' ? 'active' : ''}`}
            onClick={() => setActiveSection('upgrade')}
          >
            <span>Upgrade Level</span>
          </button>
        </div>

        {/* Right Content */}
        <div className="profile-content">
          {/* Profile Information Section */}
          {activeSection === 'profile' && (
            <div className="profile-section">
              <h2>Profile Information</h2>
              <form onSubmit={handleProfileSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={profileData.username}
                  onChange={handleProfileChange}
                  required
                  minLength={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="full_name">Full Name</label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={profileData.full_name}
                onChange={handleProfileChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={profileData.location}
                onChange={handleProfileChange}
                placeholder="e.g., San Francisco, CA"
              />
            </div>

            <div className="form-group">
              <label htmlFor="linkedin_link">LinkedIn Profile</label>
              <input
                type="url"
                id="linkedin_link"
                name="linkedin_link"
                value={profileData.linkedin_link}
                onChange={handleProfileChange}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>

            <div className="form-group">
              <label htmlFor="yc_link">Y Combinator Profile</label>
              <input
                type="url"
                id="yc_link"
                name="yc_link"
                value={profileData.yc_link}
                onChange={handleProfileChange}
                placeholder="https://ycombinator.com/..."
              />
            </div>

              <button type="submit" className="profile-button" disabled={saving}>
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </form>
          </div>
          )}

          {/* Change Password Section */}
          {activeSection === 'password' && (
            <div className="profile-section">
              <h2>Change Password</h2>
              <form onSubmit={handlePasswordSubmit}>
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                required
                autoComplete="current-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <small>Must be at least 8 characters long</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

              <button type="submit" className="profile-button" disabled={saving}>
                {saving ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
          )}

          {/* Upgrade Level Section */}
          {activeSection === 'upgrade' && (
            <div className="profile-section">
              <h2>Upgrade Level</h2>
              <div className="upgrade-info">
                <p><strong>Current Level:</strong> {profileData.level || 'Not set'}</p>
                <div className="upgrade-message">
                  <p>We are a startup and have not yet integrated payment processing. If you would like to upgrade your level, please feel free to contact us.</p>
                  <p>We are always happy to assist and will promptly upgrade your account to the level you desire.</p>
                </div>
              </div>
            </div>
          )}

          {/* Active Sessions Section */}
          {activeSection === 'sessions' && (
            <div className="profile-section">
              <h2>Active Sessions</h2>
              <p className="section-description">Manage your active login sessions across different devices</p>
              
              {loadingSessions ? (
                <div className="sessions-loading">Loading sessions...</div>
              ) : sessions.length === 0 ? (
                <div className="no-sessions">No active sessions found</div>
              ) : (
                <div className="sessions-list">
                  {sessions.map((session) => (
                    <div key={session.id} className={`session-card ${session.isCurrent ? 'current-session' : ''}`}>
                      <div className="session-info">
                        <div className="session-device">
                          <strong>{session.device}</strong>
                          {session.isCurrent && <span className="current-badge">Current Browser</span>}
                        </div>
                        <div className="session-details">
                          <div className="session-detail">
                            <span className="detail-label">Created:</span>
                            <span className="detail-value">{formatDate(session.createdAt)}</span>
                          </div>
                          <div className="session-detail">
                            <span className="detail-label">Last Active:</span>
                            <span className="detail-value">{formatDate(session.lastActive)}</span>
                          </div>
                          <div className="session-detail">
                            <span className="detail-label">Expires:</span>
                            <span className="detail-value">{formatDate(session.expiresAt)}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        className="revoke-button"
                        onClick={() => handleRevokeSession(session.id)}
                        disabled={session.isCurrent}
                        title={session.isCurrent ? "Cannot revoke current session" : "Revoke this session"}
                      >
                        {session.isCurrent ? 'Current' : 'Revoke'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
